// /admin/carga-masiva — xlsx workbook upload with preview → commit
// state machine (ADR-0024).
//
// The flow is three steps in one page (no separate routes — discards
// "which org am I loading into?" context across navigations):
//
//   pick     — file input + orgName + recipe + optional RUT
//   preview  — server dry-run returns parsed counts + warnings
//   commit   — JSON POST with { draftId, sha256 } applies it
//
// Confirmation gate before commit: simple "¿Estás seguro? ¿Crear N
// brands / M zonas en {orgName}?" with Enter to proceed (no typed-name).

import { useCallback, useEffect, useReducer, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { EvidenceRequirementPill } from "../../components/EvidenceRequirementPill";
import { Icon } from "../../components/icons";
import { LogoUploadRow } from "../../components/LogoUploadRow";
import { adminApi } from "../../lib/api/endpoints/admin";
import { ApiError } from "../../lib/api/error";
import {
  brandNameKey,
  buildLogoQueueItems,
  initialLogoQueueState,
  logoQueueReducer,
  ORG_LOGO_QUEUE_KEY,
  summarizeLogoQueue,
  type LogoItem,
  type LogoQueueAction,
  type LogoQueueState,
} from "./logoQueue";
import { LOGO_ACCEPT_ATTR, uploadLogoFile, validateLogoFile } from "./logoUpload";
import { parseWorkbookIssues } from "./parseWorkbookIssues";
import {
  invalidateAfterLogo,
  useAdminRecipes,
  useCommitWorkbook,
  usePreviewWorkbook,
} from "./queries";
import { canPreview, uploadReducer, type UploadState } from "./uploadMachine";

// ADR-0164 — optional logos: the company logo (Panel 1) and one per brand
// (Panel 2, from preview.brands). They are uploaded AFTER the commit, one
// at a time, through the queue in ./logoQueue.ts; a logo that fails or is
// skipped never touches the created org.

/** What a picked logo renders with. The File itself lives in a ref. */
type PickedLogo = { url: string; label: string; fileName: string };

/** A brand-row pick rejected by validateLogoFile, shown under the list. */
type BrandPickError = { key: string; label: string; message: string };

const LOGOS_HEADING_ID = "cm-logos-heading";
const ORG_LOGO_PICK_ID = "cm-org-logo-pick";
const LOAD_ANOTHER_ID = "cm-load-another";
const FAILURE_DEBOUNCE_MS = 500;

/** LogoUploadRow builds DOM ids from its itemKey; queue keys carry
 *  colons and brand names, so rows get index-based keys instead. */
function queueRowKey(index: number): string {
  return `cm-q${index}`;
}
function previewRowKey(index: number): string {
  return `cm-b${index}`;
}
function primaryButtonId(rowKey: string): string {
  return `logo-pick-${rowKey}`;
}

/** How the item reads inside a sentence: «el logo de la empresa». */
function spokenName(item: LogoItem): string {
  return item.key === ORG_LOGO_QUEUE_KEY ? "la empresa" : item.label;
}

function logosWord(n: number): string {
  return n === 1 ? "logo" : "logos";
}

export function AdminCargaMasiva() {
  const recipes = useAdminRecipes();
  const preview = usePreviewWorkbook();
  const commit = useCommitWorkbook();
  const qc = useQueryClient();
  const [state, dispatch] = useReducer(uploadReducer, { kind: "idle" });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [confirming, setConfirming] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // ── Picked logos ─────────────────────────────────────────────────
  // Files never enter state or a reducer: they live in this map, keyed
  // by "org", `name:<normalized brand>` (before the commit) and the queue
  // key `brand:<id>` (after it). State holds only the object URL + label.
  const logoFilesRef = useRef(new Map<string, File>());
  const pickedRef = useRef<Record<string, PickedLogo>>({});
  const [picked, setPickedState] = useState<Record<string, PickedLogo>>({});
  const orgLogoInputRef = useRef<HTMLInputElement>(null);
  const [orgPickError, setOrgPickError] = useState<string | null>(null);
  const [brandPickError, setBrandPickError] = useState<BrandPickError | null>(null);

  function setPicked(next: Record<string, PickedLogo>) {
    pickedRef.current = next;
    setPickedState(next);
  }

  function putPicked(key: string, file: File, label: string) {
    const prev = pickedRef.current[key];
    if (prev) URL.revokeObjectURL(prev.url);
    logoFilesRef.current.set(key, file);
    setPicked({
      ...pickedRef.current,
      [key]: { url: URL.createObjectURL(file), label, fileName: file.name },
    });
  }

  function dropPicked(keys: readonly string[]) {
    const next = { ...pickedRef.current };
    for (const key of keys) {
      const prev = next[key];
      if (prev) URL.revokeObjectURL(prev.url);
      delete next[key];
      logoFilesRef.current.delete(key);
    }
    setPicked(next);
  }

  // ── Post-commit queue ────────────────────────────────────────────
  // The ref mirrors the state synchronously so the sequential upload
  // loop reads a skip the operator made a moment ago.
  const queueRef = useRef<LogoQueueState>(initialLogoQueueState);
  const [queue, setQueueState] = useState<LogoQueueState>(initialLogoQueueState);
  const dispatchQueue = useCallback((action: LogoQueueAction) => {
    queueRef.current = logoQueueReducer(queueRef.current, action);
    setQueueState(queueRef.current);
  }, []);
  /** queue key → picked-logo key, for the row preview. */
  const [queueSource, setQueueSource] = useState<Record<string, string>>({});
  /** A file swapped on a queue row that validateLogoFile rejected. */
  const [queuePickErrors, setQueuePickErrors] = useState<Record<string, string>>({});
  // Bumped on reset/unmount so an in-flight upload stops dispatching.
  const runGenRef = useRef(0);
  // Every run chains onto the previous one: uploads never overlap
  // (padToSquare uses a canvas and the presign signs contentLength).
  const chainRef = useRef<Promise<void>>(Promise.resolve());
  const [confirmLeave, setConfirmLeave] = useState(false);

  // ── The one live region ──────────────────────────────────────────
  const [announcement, setAnnouncement] = useState("");
  const failedLabelsRef = useRef<string[]>([]);
  const failTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function clearFailures() {
    if (failTimerRef.current) clearTimeout(failTimerRef.current);
    failTimerRef.current = null;
    failedLabelsRef.current = [];
  }

  function announceFailure(name: string) {
    failedLabelsRef.current.push(name);
    if (failTimerRef.current) clearTimeout(failTimerRef.current);
    failTimerRef.current = setTimeout(() => {
      const names = failedLabelsRef.current;
      failedLabelsRef.current = [];
      failTimerRef.current = null;
      if (names.length === 1) {
        setAnnouncement(`Error al subir el logo de ${names[0]}. Puedes reintentar u omitir.`);
      } else if (names.length > 1) {
        setAnnouncement(`${names.length} logos no se pudieron subir. Revisa la lista.`);
      }
    }, FAILURE_DEBOUNCE_MS);
  }

  // ── Focus ────────────────────────────────────────────────────────
  const [focusRequest, setFocusRequest] = useState<{ id: string; seq: number } | null>(null);
  function requestFocus(id: string) {
    setFocusRequest((prev) => ({ id, seq: (prev?.seq ?? 0) + 1 }));
  }
  useEffect(() => {
    if (focusRequest) document.getElementById(focusRequest.id)?.focus();
  }, [focusRequest]);

  // Unmount: stop the queue, release every object URL and the timer.
  useEffect(() => {
    const gen = runGenRef;
    const pickedNow = pickedRef;
    const timer = failTimerRef;
    return () => {
      gen.current += 1;
      for (const p of Object.values(pickedNow.current)) URL.revokeObjectURL(p.url);
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  function onOrgLogoPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    e.target.value = "";
    if (!file) return;
    const invalid = validateLogoFile(file);
    if (invalid) {
      setOrgPickError(invalid);
      return;
    }
    setOrgPickError(null);
    putPicked(ORG_LOGO_QUEUE_KEY, file, "Logo de la empresa");
  }

  function clearOrgLogo() {
    dropPicked([ORG_LOGO_QUEUE_KEY]);
    setOrgPickError(null);
    requestFocus(ORG_LOGO_PICK_ID);
  }

  function onBrandLogoPick(name: string, file: File) {
    const key = brandNameKey(name);
    const invalid = validateLogoFile(file);
    if (invalid) {
      setBrandPickError({ key, label: name, message: invalid });
      return;
    }
    setBrandPickError((prev) => (prev?.key === key ? null : prev));
    putPicked(key, file, name);
  }

  function clearBrandLogo(name: string, rowKey: string) {
    const key = brandNameKey(name);
    dropPicked([key]);
    setBrandPickError((prev) => (prev?.key === key ? null : prev));
    requestFocus(primaryButtonId(rowKey));
  }

  /** A new preview can drop brands: discard their files and say so. */
  function reconcileBrandLogos(brands: readonly { name: string }[] | undefined) {
    const byKey = new Map((brands ?? []).map((b) => [brandNameKey(b.name), b.name]));
    const removed: PickedLogo[] = [];
    const next: Record<string, PickedLogo> = {};
    for (const [key, p] of Object.entries(pickedRef.current)) {
      if (key === ORG_LOGO_QUEUE_KEY) {
        next[key] = p;
        continue;
      }
      const name = byKey.get(key);
      if (name === undefined) {
        URL.revokeObjectURL(p.url);
        logoFilesRef.current.delete(key);
        removed.push(p);
      } else {
        next[key] = { ...p, label: name };
      }
    }
    setPicked(next);
    setBrandPickError(null);
    if (removed.length > 0) {
      setAnnouncement(
        removed
          .map((p) => `Quitamos el logo de «${p.label}» porque esa marca ya no está en el archivo.`)
          .join(" ")
      );
    }
  }

  async function runQueue(orgId: string, keys: readonly string[], mode: "all" | "retry") {
    const gen = runGenRef.current;
    const stale = () => gen !== runGenRef.current;
    if (stale()) return;
    for (const key of keys) {
      if (stale()) return;
      const item = queueRef.current.items.find((i) => i.key === key);
      const file = logoFilesRef.current.get(key);
      if (!item || !item.target || !file) continue;
      if (item.status !== "pending" && item.status !== "error") continue;
      dispatchQueue({ type: "start", key });
      try {
        await uploadLogoFile(orgId, item.target, file);
        if (stale()) return;
        // The row's primary button disables once done — move focus off
        // it first so it never falls to <body>.
        const index = queueRef.current.items.findIndex((i) => i.key === key);
        const hadFocus = document.activeElement?.id === primaryButtonId(queueRowKey(index));
        dispatchQueue({ type: "success", key });
        if (hadFocus) requestFocus(LOGOS_HEADING_ID);
        if (mode === "retry") setAnnouncement(`Logo de ${spokenName(item)} subido.`);
      } catch (err) {
        if (stale()) return;
        dispatchQueue({
          type: "fail",
          key,
          error: err instanceof Error ? err.message : String(err),
        });
        announceFailure(spokenName(item));
      }
    }
    if (stale()) return;
    void invalidateAfterLogo(qc, orgId);
    if (mode === "all") {
      // The closing line already counts the failures.
      clearFailures();
      const s = summarizeLogoQueue(queueRef.current);
      setAnnouncement(
        s.error === 0 && s.skipped === 0
          ? `Logos subidos: ${s.done} de ${s.total}.`
          : `Cola terminada. ${s.done} subidos, ${s.skipped} omitidos, ${s.error} con error.`
      );
    }
  }

  function enqueueRun(orgId: string, keys: readonly string[], mode: "all" | "retry") {
    const run = chainRef.current.then(() => runQueue(orgId, keys, mode));
    chainRef.current = run.catch(() => undefined);
    return run;
  }

  function queueRowIdFor(key: string): string | null {
    const index = queueRef.current.items.findIndex((i) => i.key === key);
    return index < 0 ? null : primaryButtonId(queueRowKey(index));
  }

  function handleRetry(orgId: string, key: string) {
    const id = queueRowIdFor(key);
    if (id) requestFocus(id);
    void enqueueRun(orgId, [key], "retry");
  }

  function handleSkip(key: string) {
    dispatchQueue({ type: "skip", key });
    const items = queueRef.current.items;
    const index = items.findIndex((i) => i.key === key);
    const ordered = [...items.slice(index + 1), ...items.slice(0, Math.max(index, 0))];
    const next = ordered.find(
      (i) => i.status === "pending" || (i.status === "error" && i.retryable)
    );
    requestFocus(next ? (queueRowIdFor(next.key) ?? LOGOS_HEADING_ID) : LOGOS_HEADING_ID);
  }

  function handleSkipAll() {
    dispatchQueue({ type: "skipAll" });
    requestFocus(LOGOS_HEADING_ID);
  }

  function handleQueuePick(orgId: string, item: LogoItem, file: File) {
    const invalid = validateLogoFile(file);
    if (invalid) {
      setQueuePickErrors((prev) => ({ ...prev, [item.key]: invalid }));
      return;
    }
    setQueuePickErrors((prev) => {
      const next = { ...prev };
      delete next[item.key];
      return next;
    });
    const source = queueSource[item.key] ?? item.key;
    putPicked(source, file, pickedRef.current[source]?.label ?? item.label);
    logoFilesRef.current.set(item.key, file);
    if (item.status === "error" && item.retryable) handleRetry(orgId, item.key);
  }

  async function handleDownloadTemplate() {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await adminApi.downloadEmptyTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "bitacorapro-template-vacio.xlsx";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(
        err instanceof ApiError
          ? err.message
          : "No pudimos descargar la plantilla. Intenta de nuevo."
      );
    } finally {
      setDownloading(false);
    }
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) dispatch({ type: "file-picked", file });
  }

  async function handlePreview() {
    if (state.kind !== "file-picked" && state.kind !== "preview-error") return;
    if (!canPreview(state)) return;
    dispatch({ type: "preview-start" });
    try {
      const res = await preview.mutateAsync({
        file: state.file,
        orgName: state.orgName.trim(),
        recipeSlug: state.recipeSlug,
        ...(state.rut.trim() ? { rut: state.rut.trim() } : {}),
      });
      dispatch({ type: "preview-success", preview: res });
      reconcileBrandLogos(res.brands);
    } catch (err) {
      dispatch({
        type: "preview-fail",
        error: err instanceof ApiError ? err.message : "No pudimos parsear el xlsx.",
        issues: err instanceof ApiError ? parseWorkbookIssues(err.details) : undefined,
      });
    }
  }

  async function handleCommit() {
    if (state.kind !== "preview-ready") return;
    const previewBrands = state.preview.brands ?? [];
    setConfirming(false);
    dispatch({ type: "commit-start" });
    try {
      const result = await commit.mutateAsync({
        draftId: state.preview.draftId,
        sha256: state.preview.sha256,
      });
      dispatch({ type: "commit-success", result });
      startLogoQueue(
        result.organizationId,
        previewBrands.map((b) => b.name),
        result.brands
      );
    } catch (err) {
      dispatch({
        type: "commit-fail",
        error: err instanceof ApiError ? err.message : "No pudimos commitear el draft.",
      });
    }
  }

  /** After the commit: company logo first, then each brand with a file,
   *  in the order of the file — one upload at a time. */
  function startLogoQueue(
    orgId: string,
    previewBrandNames: readonly string[],
    commitBrands: Parameters<typeof buildLogoQueueItems>[0]["commitBrands"]
  ) {
    const files = logoFilesRef.current;
    const includeOrg = files.has(ORG_LOGO_QUEUE_KEY);
    const brandNames = previewBrandNames.filter((name) => files.has(brandNameKey(name)));
    if (!includeOrg && brandNames.length === 0) return;

    const items = buildLogoQueueItems({ includeOrg, brandNames, commitBrands });
    const source: Record<string, string> = {};
    for (const item of items) {
      const from = item.key === ORG_LOGO_QUEUE_KEY ? item.key : brandNameKey(item.label);
      source[item.key] = from;
      const file = files.get(from);
      if (file) files.set(item.key, file);
    }
    setQueueSource(source);
    setQueuePickErrors({});
    dispatchQueue({ type: "init", items });
    const uploadable = items.filter((i) => i.target !== null).length;
    if (uploadable > 0) setAnnouncement(`Subiendo ${uploadable} ${logosWord(uploadable)}.`);
    void enqueueRun(
      orgId,
      items.map((i) => i.key),
      "all"
    );
  }

  function handleReset() {
    if (fileInputRef.current) fileInputRef.current.value = "";
    setConfirming(false);
    setConfirmLeave(false);
    runGenRef.current += 1;
    chainRef.current = Promise.resolve();
    for (const p of Object.values(pickedRef.current)) URL.revokeObjectURL(p.url);
    logoFilesRef.current.clear();
    setPicked({});
    setOrgPickError(null);
    setBrandPickError(null);
    setQueueSource({});
    setQueuePickErrors({});
    dispatchQueue({ type: "reset" });
    clearFailures();
    setAnnouncement("");
    dispatch({ type: "reset" });
  }

  const queueSummary = summarizeLogoQueue(queue);
  const queueBusy = queueSummary.pending + queueSummary.uploading > 0;

  function handleLoadAnother() {
    if (queueBusy) {
      setConfirmLeave(true);
      return;
    }
    handleReset();
  }

  function handleWait() {
    setConfirmLeave(false);
    requestFocus(LOAD_ANOTHER_ID);
  }

  const isDone = isDoneState(state);

  return (
    <div className="page">
      <div className="page-pad">
        <header className="page-header">
          <div className="page-header__lead">
            <div className="page-header__eyebrow">Platform admin</div>
            <h1 className="page-header__title">Carga masiva</h1>
            <p className="page-header__sub">
              Sube la planilla xlsx de onboarding. El server hace dry-run (preview); revisas los
              conteos; confirmas el commit.
            </p>
          </div>
          {state.kind !== "idle" && !isDone && (
            <div className="page-header__actions">
              <button type="button" className="btn btn--ghost" onClick={handleReset}>
                Empezar de nuevo
              </button>
            </div>
          )}
        </header>

        {isDoneState(state) ? (
          <CommitDoneCard
            state={state}
            logos={
              queue.items.length > 0 ? (
                <LogoQueueSection
                  items={queue.items}
                  previewUrlFor={(key) => picked[queueSource[key] ?? key]?.url ?? null}
                  pickErrorFor={(key) => queuePickErrors[key]}
                  onPick={(item, file) => handleQueuePick(state.result.organizationId, item, file)}
                  onRetry={(key) => handleRetry(state.result.organizationId, key)}
                  onSkip={handleSkip}
                  onSkipAll={handleSkipAll}
                />
              ) : null
            }
            confirmLeave={confirmLeave}
            onLoadAnother={handleLoadAnother}
            onWait={handleWait}
            onLeave={handleReset}
          />
        ) : (
          <>
            <section className="panel">
              <div className="panel__head">
                <h2 className="panel__title">1 · Archivo y destino</h2>
              </div>
              <div className="panel__body">
                <div className="field">
                  <label className="field__label" htmlFor="cm-file">
                    Planilla xlsx
                  </label>
                  <input
                    id="cm-file"
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={onFileChange}
                  />
                  {state.kind !== "idle" && "file" in state && (
                    <p className="cell-meta" style={{ marginTop: 6, fontSize: "var(--t-12)" }}>
                      {state.file.name} · {(state.file.size / 1024).toFixed(1)} KB
                    </p>
                  )}
                  <div
                    style={{
                      marginTop: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <span className="cell-meta" style={{ fontSize: "var(--t-12)" }}>
                      ¿No tienes la planilla?
                    </span>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      disabled={downloading}
                      onClick={() => void handleDownloadTemplate()}
                    >
                      <Icon.Download s={13} />
                      {downloading ? "Descargando…" : "Descargar plantilla"}
                    </button>
                  </div>
                  {downloadError && (
                    <p className="auth-error" role="alert" style={{ marginTop: 8 }}>
                      {downloadError}
                    </p>
                  )}
                </div>

                {(state.kind === "file-picked" ||
                  state.kind === "preview-pending" ||
                  state.kind === "preview-ready" ||
                  state.kind === "preview-error") && (
                  <>
                    <div className="field">
                      <label className="field__label" htmlFor="cm-org">
                        Nombre de la organización
                      </label>
                      <input
                        id="cm-org"
                        className="input"
                        type="text"
                        value={state.orgName}
                        onChange={(e) =>
                          dispatch({
                            type: "set-meta",
                            field: "orgName",
                            value: e.target.value,
                          })
                        }
                        placeholder="Falabella Retail"
                      />
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor="cm-recipe">
                        Recipe (vertical)
                      </label>
                      <select
                        id="cm-recipe"
                        className="select"
                        value={state.recipeSlug}
                        onChange={(e) =>
                          dispatch({
                            type: "set-meta",
                            field: "recipeSlug",
                            value: e.target.value,
                          })
                        }
                      >
                        <option value="">Selecciona un recipe…</option>
                        {recipes.data?.recipes.map((r) => (
                          <option key={r.slug} value={r.slug}>
                            {r.slug} — {r.organizationLabel}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor="cm-rut">
                        RUT (opcional)
                      </label>
                      <input
                        id="cm-rut"
                        className="input"
                        type="text"
                        value={state.rut}
                        onChange={(e) =>
                          dispatch({
                            type: "set-meta",
                            field: "rut",
                            value: e.target.value,
                          })
                        }
                        placeholder="76.123.456-7"
                      />
                    </div>

                    <div
                      className="field"
                      role="group"
                      aria-labelledby="cm-org-logo-label"
                      aria-describedby="cm-org-logo-help"
                    >
                      <span id="cm-org-logo-label" className="field__label">
                        Logo de la empresa (opcional)
                      </span>
                      <ul className="brand-list">
                        <li className="brand-list__row">
                          <div className="brand-list__preview">
                            {picked[ORG_LOGO_QUEUE_KEY] ? (
                              <img
                                src={picked[ORG_LOGO_QUEUE_KEY].url}
                                alt="Vista previa del logo de la empresa"
                                className="brand-list__img"
                              />
                            ) : (
                              <span className="cell-meta">Sin logo</span>
                            )}
                          </div>
                          <div className="brand-list__meta">
                            <span id="cm-org-logo-help" className="cell-meta">
                              Logo corporativo. Se muestra en el menú lateral de quienes ven toda la
                              organización. Puedes subirlo después.
                            </span>
                            <span className="field__hint">
                              {picked[ORG_LOGO_QUEUE_KEY]?.fileName ??
                                "PNG, JPG o WEBP de hasta 2 MB."}
                            </span>
                          </div>
                          <div className="brand-list__actions">
                            <input
                              ref={orgLogoInputRef}
                              type="file"
                              hidden
                              tabIndex={-1}
                              aria-hidden="true"
                              accept={LOGO_ACCEPT_ATTR}
                              onChange={onOrgLogoPick}
                            />
                            <button
                              id={ORG_LOGO_PICK_ID}
                              type="button"
                              className={`btn btn--sm ${
                                picked[ORG_LOGO_QUEUE_KEY] ? "btn--secondary" : "btn--primary"
                              }`}
                              aria-label={
                                picked[ORG_LOGO_QUEUE_KEY]
                                  ? "Cambiar logo de la empresa"
                                  : "Elegir logo de la empresa"
                              }
                              onClick={() => orgLogoInputRef.current?.click()}
                            >
                              {picked[ORG_LOGO_QUEUE_KEY] ? "Cambiar" : "Elegir logo"}
                            </button>
                            {picked[ORG_LOGO_QUEUE_KEY] && (
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                aria-label="Quitar logo de la empresa"
                                onClick={clearOrgLogo}
                              >
                                Quitar
                              </button>
                            )}
                          </div>
                        </li>
                      </ul>
                      {orgPickError && (
                        <p className="auth-error" role="alert">
                          {orgPickError}
                        </p>
                      )}
                    </div>

                    {state.kind === "preview-error" && (
                      <>
                        <p className="auth-error" role="alert">
                          {state.error}
                        </p>
                        {state.issues && state.issues.length > 0 && (
                          <ul className="workbook-issue-list">
                            {state.issues.map((issue, i) => (
                              <li key={i} className="workbook-issue-list__item">
                                <code>{issue.scope}</code>
                                {issue.rowIndex !== undefined ? ` · fila ${issue.rowIndex}` : ""}
                                {issue.column ? ` · columna ${issue.column}` : ""}: {issue.message}
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}

                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        className="btn btn--primary"
                        disabled={!canPreview(state) || state.kind === "preview-pending"}
                        onClick={() => void handlePreview()}
                      >
                        {state.kind === "preview-pending"
                          ? "Procesando…"
                          : state.kind === "preview-ready"
                            ? "Re-procesar"
                            : "Generar vista previa"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </section>

            {state.kind === "preview-ready" && (
              <section className="panel">
                <div className="panel__head">
                  <h2 className="panel__title">2 · Vista previa</h2>
                  <p className="panel__sub">
                    Draft <code>{state.preview.draftId.slice(0, 8)}</code> · expira{" "}
                    {new Date(state.preview.expiresAt).toLocaleString("es-CL")}
                  </p>
                </div>
                <div className="panel__body">
                  <PreviewCounts preview={state.preview} />
                  {state.preview.warnings.length > 0 && (
                    <details
                      style={{
                        marginTop: 12,
                        padding: "10px 12px",
                        background: "var(--paper-2)",
                        borderRadius: 8,
                      }}
                    >
                      <summary>
                        {state.preview.warnings.length} advertencia
                        {state.preview.warnings.length === 1 ? "" : "s"} no bloqueante
                        {state.preview.warnings.length === 1 ? "" : "s"}
                      </summary>
                      <ul
                        style={{
                          marginTop: 8,
                          paddingLeft: 18,
                          fontSize: "var(--t-12)",
                        }}
                      >
                        {state.preview.warnings.map((w, i) => (
                          <li key={i}>
                            <code>{w.scope}</code>
                            {w.rowIndex !== undefined ? ` · fila ${w.rowIndex}` : ""}: {w.message}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}

                  {state.preview.brands && state.preview.brands.length > 0 && (
                    <section
                      className="stack-gap stack-gap--sm"
                      style={{ marginTop: "var(--s-5)" }}
                      aria-labelledby="cm-brand-logos-title"
                    >
                      <h3 id="cm-brand-logos-title" className="panel__title">
                        Logos por marca (opcional)
                      </h3>
                      <p className="cell-meta">
                        Cada logo se muestra en el menú lateral del gerente de esa marca y de
                        quienes solo tienen tiendas de esa marca. Si una marca no tiene logo, verán
                        el de la empresa.
                      </p>
                      <ul className="brand-list">
                        {state.preview.brands.map((brand, index) => {
                          const rowKey = previewRowKey(index);
                          const chosen = picked[brandNameKey(brand.name)];
                          return (
                            <LogoUploadRow
                              key={brandNameKey(brand.name)}
                              itemKey={rowKey}
                              title={brand.name}
                              pill={
                                brand.isDefault ? (
                                  <span className="st-pill st-pill--brand">Marca principal</span>
                                ) : undefined
                              }
                              previewUrl={chosen?.url ?? null}
                              primaryLabel={chosen ? "Cambiar" : "Elegir logo"}
                              onPick={(file) => onBrandLogoPick(brand.name, file)}
                              extraActions={
                                chosen ? (
                                  <button
                                    id={`cm-brand-remove-${index}`}
                                    type="button"
                                    className="btn btn--ghost btn--sm"
                                    aria-labelledby={`cm-brand-remove-${index} logo-name-${rowKey}`}
                                    onClick={() => clearBrandLogo(brand.name, rowKey)}
                                  >
                                    Quitar
                                  </button>
                                ) : undefined
                              }
                            />
                          );
                        })}
                      </ul>
                      {brandPickError && (
                        <p className="auth-error" role="alert">
                          «{brandPickError.label}»: {brandPickError.message}
                        </p>
                      )}
                    </section>
                  )}

                  <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                    {!confirming ? (
                      <button
                        type="button"
                        className="btn btn--primary"
                        onClick={() => setConfirming(true)}
                      >
                        Confirmar y crear organización
                      </button>
                    ) : (
                      <ConfirmCommit
                        orgName={state.orgName}
                        preview={state.preview}
                        committing={commit.isPending}
                        onCancel={() => setConfirming(false)}
                        onConfirm={() => void handleCommit()}
                      />
                    )}
                  </div>
                </div>
              </section>
            )}

            {state.kind === "commit-pending" && (
              <section className="panel">
                <div className="panel__body">
                  {/* Mutation progress (the commit is running), not a screen load:
                      a plain status line, no skeleton. */}
                  <p className="cell-meta">Creando organización…</p>
                </div>
              </section>
            )}

            {state.kind === "commit-error" && (
              <section className="panel">
                <div className="panel__body">
                  <p className="auth-error" role="alert">
                    {state.error}
                  </p>
                  <button type="button" className="btn btn--secondary" onClick={handleReset}>
                    Empezar de nuevo
                  </button>
                </div>
              </section>
            )}
          </>
        )}

        {/* The page's only live region (ADR-0164 logo queue). Mounted
            from the first render and never unmounted, so the first
            announcement is not lost on a region that just appeared. */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {announcement}
        </div>
      </div>
    </div>
  );
}

function PreviewCounts({
  preview,
}: {
  preview: NonNullable<ReturnType<typeof usePreviewWorkbook>["data"]>;
}) {
  // Distinct emails the commit will fan out as invitations: deduped union
  // of Managers + Tiendas jefe + Tiendas supervisor. This — not the
  // Managers-sheet row count — is how many emails Resend will send.
  const invites = preview.counts.distinctInvitationEmails;
  // Resend free-tier daily cap. Surfaced so the operator can split a large
  // org across days rather than silently hit the wall on commit.
  const RESEND_DAILY_CAP = 100;
  const overCap = invites > RESEND_DAILY_CAP;

  // ADR-0088 §Ingest follow-up. Only shown when the workbook actually has
  // checklist items — residential_rental/broker_postventa never do, and a
  // "0 ítems con evidencia requerida" row for a vertical with no concept
  // of items reads as a bug report, not information.
  const evidenceCount = preview.counts.itemsWithEvidenceRequirement;
  const showEvidenceCount = preview.counts.items > 0;

  const items: { label: string; n: number }[] = [
    { label: "Marcas", n: preview.counts.brands },
    { label: "Tiendas", n: preview.counts.tiendas },
    { label: "Categorías", n: preview.counts.categories },
    { label: "Items checklist", n: preview.counts.items },
    { label: "Contratistas", n: preview.counts.providers },
    { label: "Managers", n: preview.counts.usuarios },
    { label: "Mantenciones", n: preview.counts.mantenciones },
    { label: "Invitaciones a enviar", n: invites },
    ...(showEvidenceCount ? [{ label: "Ítems con evidencia requerida", n: evidenceCount }] : []),
  ];
  return (
    <>
      <ul
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
          gap: 8,
          listStyle: "none",
          padding: 0,
          margin: 0,
        }}
      >
        {items.map((it) => (
          <li
            key={it.label}
            style={{
              padding: "10px 12px",
              background: "var(--paper-2)",
              borderRadius: 8,
            }}
          >
            <div className="cell-meta" style={{ fontSize: "var(--t-12)" }}>
              {it.label}
            </div>
            <div className="cell-strong" style={{ fontSize: 20 }}>
              {it.n}
            </div>
          </li>
        ))}
      </ul>
      <p
        className="cell-meta"
        role={overCap ? "alert" : undefined}
        style={{
          marginTop: 8,
          marginBottom: 0,
          fontSize: "var(--t-12)",
          color: overCap ? "var(--danger-fg)" : undefined,
        }}
      >
        {overCap
          ? `${invites} invitaciones superan el límite de ${RESEND_DAILY_CAP} emails/día de Resend. Considera dividir la carga en varios días.`
          : `El commit enviará ${invites} invitaciones por email (límite Resend: ${RESEND_DAILY_CAP}/día).`}
      </p>
      {/* ADR-0088 §Ingest follow-up. Visible by default (not behind
          <details>) — this changes what the field team can close in the
          app of captura, it's a decision input, not a diagnostic detail. */}
      {showEvidenceCount && evidenceCount > 0 && (
        <p className="cell-meta" style={{ marginTop: 6, marginBottom: 0, fontSize: "var(--t-12)" }}>
          {evidenceCount} de {preview.counts.items} ítems del checklist van a requerir evidencia
          (foto y/o nota de voz) para poder cerrarse en la app de captura.
        </p>
      )}
      {preview.evidenceByBrand && preview.evidenceByBrand.length > 0 && (
        <details className="workbook-issue-list" style={{ paddingLeft: 0, marginTop: 10 }}>
          <summary style={{ paddingLeft: "var(--s-5)" }}>
            Ver desglose de evidencia requerida por ítem
          </summary>
          {preview.evidenceByBrand.map((b) => (
            <div key={b.brandName} style={{ marginTop: 8, paddingLeft: "var(--s-5)" }}>
              <strong>{b.brandName}</strong>
              {b.areas.map((a) => (
                <div key={a.sectionLabel} style={{ marginTop: 4 }}>
                  <span className="cell-meta">{a.sectionLabel}</span>
                  <ul style={{ marginTop: 2, paddingLeft: 18 }}>
                    {a.items.map((it) => (
                      <li key={it.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {/* ADR-0173 (D9): the client's item code, or a warning
                            when the row has none — the commit will refuse it. */}
                        {it.clientCode ? (
                          <code className="cell-meta">{it.clientCode}</code>
                        ) : (
                          <span className="cell-meta" title="Sin código: la carga lo exige">
                            sin código
                          </span>
                        )}
                        {it.label}
                        <EvidenceRequirementPill evidenceRequirement={it.evidenceRequirement} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </details>
      )}
    </>
  );
}

function ConfirmCommit({
  orgName,
  preview,
  committing,
  onCancel,
  onConfirm,
}: {
  orgName: string;
  preview: NonNullable<ReturnType<typeof usePreviewWorkbook>["data"]>;
  committing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const evidenceCount = preview.counts.itemsWithEvidenceRequirement;
  const hasEvidenceImpact = evidenceCount > 0;
  // ADR-0088 §Ingest follow-up. When this commit activates the evidence
  // gate on at least one item, the operator must explicitly acknowledge
  // it before "Sí, crear" enables — a checkbox, not just text, because
  // the consequence lands on whoever opens the app de captura next, not
  // on the person clicking here. No extra friction when the workbook has
  // no evidence-gated items (hasEvidenceImpact === false).
  const [acknowledged, setAcknowledged] = useState(false);
  const confirmDisabled = committing || (hasEvidenceImpact && !acknowledged);

  const describedByIds = ["cm-confirm-desc", hasEvidenceImpact ? "cm-confirm-gate-warning" : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="cm-confirm-title"
      aria-describedby={describedByIds}
      className={hasEvidenceImpact ? "workbook-confirm-card--warn" : undefined}
      style={
        hasEvidenceImpact
          ? undefined
          : {
              padding: 14,
              border: "1px solid var(--line)",
              borderRadius: 8,
              background: "var(--paper-2)",
            }
      }
    >
      {hasEvidenceImpact && (
        <div className="workbook-confirm-card__eyebrow">
          <Icon.Warning s={13} />
          Acción con consecuencia en terreno
        </div>
      )}
      <p id="cm-confirm-title" style={{ marginTop: 0 }}>
        ¿Crear <strong>{orgName}</strong> con <strong>{preview.counts.tiendas}</strong> tiendas y{" "}
        <strong>{preview.counts.brands}</strong> marcas?
      </p>
      <p
        id="cm-confirm-desc"
        className="cell-meta"
        style={{ marginBottom: 12, fontSize: "var(--t-12)" }}
      >
        Esta acción crea el árbol de la organización + invitaciones. No se puede deshacer desde el
        panel.
      </p>
      {hasEvidenceImpact && (
        <>
          <p id="cm-confirm-gate-warning" style={{ marginTop: 0, marginBottom: 12 }}>
            Esta carga activa el requisito de evidencia en {evidenceCount} ítem
            {evidenceCount === 1 ? "" : "s"} de checklist. Desde el commit, el equipo en terreno no
            va a poder cerrar esos bloques en la app de captura sin la evidencia definida (foto,
            nota de voz, o ambas) por cada ítem.
          </p>
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              marginBottom: 12,
              fontSize: "var(--t-13)",
            }}
          >
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            Entiendo que esto cambia lo que el equipo en terreno puede cerrar en la app de captura.
          </label>
        </>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={onCancel}
          disabled={committing}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn--primary"
          onClick={onConfirm}
          disabled={confirmDisabled}
          autoFocus={!hasEvidenceImpact}
        >
          {committing ? "Creando…" : "Sí, crear"}
        </button>
      </div>
    </div>
  );
}

type DoneState = Extract<UploadState, { kind: "commit-done" }>;

function isDoneState(s: UploadState): s is DoneState {
  return s.kind === "commit-done";
}

function LogoQueueSection({
  items,
  previewUrlFor,
  pickErrorFor,
  onPick,
  onRetry,
  onSkip,
  onSkipAll,
}: {
  items: LogoItem[];
  previewUrlFor: (key: string) => string | null;
  pickErrorFor: (key: string) => string | undefined;
  onPick: (item: LogoItem, file: File) => void;
  onRetry: (key: string) => void;
  onSkip: (key: string) => void;
  onSkipAll: () => void;
}) {
  const summary = summarizeLogoQueue({ items });
  const settled = summary.pending + summary.uploading === 0;
  // A brand the commit did not return (retryable false) can't move:
  // «Omitir todos» is for what is still in play.
  const showSkipAll = items.some(
    (i) =>
      i.status === "pending" || i.status === "uploading" || (i.status === "error" && i.retryable)
  );
  let closing: string | null = null;
  if (settled) {
    if (summary.done === summary.total) closing = "Se subieron todos los logos.";
    else if (summary.skipped === summary.total)
      closing =
        "Omitiste los logos. Puedes subirlos cuando quieras desde la ficha de la organización.";
    else
      closing = `Se subieron ${summary.done} de ${summary.total} logos. Puedes subir el resto desde la ficha de la organización.`;
  }

  return (
    <section
      className="stack-gap stack-gap--sm"
      style={{ marginTop: "var(--s-5)" }}
      aria-labelledby={LOGOS_HEADING_ID}
    >
      <p className="notice-banner">
        La organización ya está creada. Los logos se suben aparte; si alguno falla, puedes
        reintentarlo aquí o subirlo después desde la ficha de la organización.
      </p>
      <h2 id={LOGOS_HEADING_ID} tabIndex={-1} className="panel__title">
        Logos
      </h2>
      <ul className="brand-list">
        {items.map((item, index) => (
          <LogoUploadRow
            key={item.key}
            itemKey={queueRowKey(index)}
            title={item.label}
            previewUrl={previewUrlFor(item.key)}
            status={{ kind: item.status, message: pickErrorFor(item.key) ?? item.error }}
            primaryLabel="Cambiar archivo"
            onPick={(file) => onPick(item, file)}
            onRetry={item.retryable ? () => onRetry(item.key) : undefined}
            onSkip={item.retryable ? () => onSkip(item.key) : undefined}
            disabled={!item.retryable || item.status === "done" || item.status === "skipped"}
          />
        ))}
      </ul>
      {showSkipAll && (
        <div className="brand-list__actions">
          <button type="button" className="btn btn--ghost btn--sm" onClick={onSkipAll}>
            Omitir todos
          </button>
        </div>
      )}
      {closing && <p className="cell-meta">{closing}</p>}
    </section>
  );
}

function CommitDoneCard({
  state,
  logos,
  confirmLeave,
  onLoadAnother,
  onWait,
  onLeave,
}: {
  state: DoneState;
  logos: ReactNode;
  confirmLeave: boolean;
  onLoadAnother: () => void;
  onWait: () => void;
  onLeave: () => void;
}) {
  const { result } = state;
  const hasInvitationErrors = result.invitationErrors.length > 0;
  return (
    <section className="panel">
      <div className="panel__head">
        <h2 className="panel__title">Organización creada</h2>
        <p className="panel__sub">
          ID <code>{result.organizationId}</code>
        </p>
      </div>
      <div className="panel__body">
        <ul style={{ paddingLeft: 18 }}>
          <li>{result.brandsCreated} marcas</li>
          <li>{result.zonesCreated} zonas</li>
          <li>{result.storesCreated} tiendas</li>
          <li>{result.categoriesWritten} categorías</li>
          <li>
            Checklist:{" "}
            {result.checklistByBrand.reduce(
              (n, b) => n + b.areas.reduce((m, a) => m + a.itemCount, 0),
              0
            )}{" "}
            ítems en {result.checklistByBrand.length}{" "}
            {result.checklistByBrand.length === 1 ? "marca" : "marcas"} (
            {result.itemCategoryLinksCreated} vínculos con categorías)
          </li>
          <li>
            {result.providersCreated} contratistas ({result.providerContactsCreated} contactos,{" "}
            {result.providerCategoryLinksCreated} vínculos con categorías)
          </li>
          <li>{result.maintenanceItemsCreated} ítems de mantención</li>
          <li>{result.invitationsQueued} invitaciones enviadas</li>
          {result.evidenceRequirementItemsCreated > 0 && (
            <li>
              Requiere evidencia: {result.evidenceRequirementItemsCreated}{" "}
              {result.evidenceRequirementItemsCreated === 1 ? "ítem" : "ítems"}
            </li>
          )}
        </ul>
        {result.checklistByBrand.length > 0 && (
          <details
            style={{
              marginTop: 8,
              padding: "8px 12px",
              background: "var(--paper-2)",
              borderRadius: 8,
            }}
          >
            <summary style={{ fontSize: "var(--t-13)" }}>Bloques por marca</summary>
            <ul style={{ marginTop: 6, paddingLeft: 18, fontSize: "var(--t-13)" }}>
              {result.checklistByBrand.map((b) => (
                <li key={b.brandName}>
                  <strong>{b.brandName}</strong>:{" "}
                  {b.areas.length === 0
                    ? "sin ítems"
                    : b.areas.map((a) => `${a.sectionLabel} (${a.itemCount})`).join(", ")}
                </li>
              ))}
            </ul>
          </details>
        )}
        {!result.configApplied && (
          <p className="auth-error" role="alert">
            Config de la org no se aplicó: {result.configError ?? "razón desconocida"}. Reintenta
            desde la pantalla de organización.
          </p>
        )}
        {hasInvitationErrors && (
          <details
            style={{
              marginTop: 12,
              padding: "10px 12px",
              background: "var(--paper-2)",
              borderRadius: 8,
            }}
          >
            <summary>
              {result.invitationErrors.length} invitación
              {result.invitationErrors.length === 1 ? "" : "es"} fallida
              {result.invitationErrors.length === 1 ? "" : "s"}
            </summary>
            <ul
              style={{
                marginTop: 8,
                paddingLeft: 18,
                fontSize: "var(--t-12)",
              }}
            >
              {result.invitationErrors.map((e, i) => (
                <li key={i}>
                  <code>{e.email}</code> ({e.role}): {e.error}
                </li>
              ))}
            </ul>
          </details>
        )}

        {logos}

        <div className="brand-list__actions" style={{ marginTop: "var(--s-4)" }}>
          <Link className="btn btn--secondary" to={`/admin/orgs/${result.organizationId}`}>
            Ver organización
          </Link>
          <button
            id={LOAD_ANOTHER_ID}
            type="button"
            className="btn btn--ghost"
            onClick={onLoadAnother}
          >
            Cargar otra
          </button>
        </div>
        {confirmLeave && (
          <div
            className="stack-gap stack-gap--sm"
            style={{ marginTop: "var(--s-3)" }}
            role="group"
            aria-labelledby="cm-leave-text"
          >
            <p id="cm-leave-text" className="notice-banner">
              Hay logos subiéndose. Si sales ahora se cancelan.
            </p>
            <div className="brand-list__actions">
              <button type="button" className="btn btn--primary btn--sm" onClick={onWait} autoFocus>
                Esperar
              </button>
              <button type="button" className="btn btn--ghost btn--sm" onClick={onLeave}>
                Salir igual
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
